import {
  useGetInvoiceFilesReportStatuses,
  useGetInvoiceReportStatuses,
} from '@/context/services/reportsType/invoiceReports/Edit.service';
import { BaseOption } from '@/hooks/useTypeToOptions';
import {
  getGetInvoiceFilesStatusesQueryKey,
  getGetInvoiceReportStatusesQueryKey,
  InvoiceFilesStatusesResponse,
  InvoiceReportStatusesResponse,
} from 'ap-openapi';
import { AxiosResponse } from 'axios';
import { useCallback } from 'react';
import { useGetAllCacheDataWithQueryKey } from '../useGetAllCacheDataWithQueryKey';
import {
  CacheItem,
  findOne,
  toItems,
  useBaseValueRetrieval,
} from './useBaseValueRetrieval';

// ============================================================================
// Types
// ============================================================================

type StatusResponse = AxiosResponse<InvoiceReportStatusesResponse, unknown>;
type FilesStatusResponse = AxiosResponse<InvoiceFilesStatusesResponse, unknown>;

// ============================================================================
// Config
// ============================================================================

const INVOICE_KEYS = {
  status: getGetInvoiceReportStatusesQueryKey()[0],
  filesStatus: getGetInvoiceFilesStatusesQueryKey()[0],
} as const;

// ============================================================================
// Extractors
// ============================================================================

const extractStatus = (data: StatusResponse): CacheItem[] =>
  (data?.data ?? []).map((item) => ({ value: item.value, name: item.name }));

const extractFilesStatus = (data: FilesStatusResponse): CacheItem[] =>
  (data?.data ?? []).map((item) => ({ value: item.value, name: item.name }));

// ============================================================================
// Hook
// ============================================================================

/**
 * Invoice-specific value retrieval with prefetch
 * Extends useBaseValueRetrieval with invoice status lookups
 *
 * @example
 * const { getApPayeeValue, getInvoiceStatusValues } = useInvoiceValueRetrieval();
 */
export const useInvoiceValueRetrieval = () => {
  const base = useBaseValueRetrieval();
  const { getAllQueriesData } = useGetAllCacheDataWithQueryKey();

  // Prefetch invoice statuses on mount
  useGetInvoiceReportStatuses();
  useGetInvoiceFilesReportStatuses();

  const getInvoiceStatusValues = useCallback(
    (value: string, options?: BaseOption[]): string => {
      const data = getAllQueriesData<StatusResponse>([INVOICE_KEYS.status]);
      return (
        findOne(data, extractStatus, value, toItems(options, true)) ?? value
      );
    },
    [getAllQueriesData]
  );

  const getInvoiceFilesStatusValues = useCallback(
    ({
      value,
      invoiceFilesStatusOptions,
      isHideIfNoLabel = false,
    }: {
      value: string;
      invoiceFilesStatusOptions?: BaseOption[];
      isHideIfNoLabel?: boolean;
    }): string => {
      if (!value) return isHideIfNoLabel ? '' : value;
      const data = getAllQueriesData<FilesStatusResponse>([
        INVOICE_KEYS.filesStatus,
      ]);
      const found = findOne(
        data,
        extractFilesStatus,
        value,
        toItems(invoiceFilesStatusOptions, true)
      );
      return found ?? (isHideIfNoLabel ? '' : value);
    },
    [getAllQueriesData]
  );

  return {
    ...base,
    getInvoiceStatusValues,
    getInvoiceFilesStatusValues,
  };
};
